import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useLocation } from "wouter";
import {
  useListAdminAssessments,
  getListAdminAssessmentsQueryKey,
  customFetch,
} from "@workspace/api-client-react";
import type { ListAdminAssessmentsParams } from "@workspace/api-client-react";
import { useMutation } from "@tanstack/react-query";
import type { AdminAssessment } from "@workspace/api-client-react";
import { useAdminAuth } from "@/contexts/admin-auth";
import AdminLayout from "@/components/admin-layout";
import { NewAssessmentDialog } from "@/components/new-assessment-dialog";
import { useDebounce } from "@/hooks/use-debounce";
import { STATUS_LABEL, STATUS_CLASS } from "@/constants/session-status";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink, ChevronRight, Send, Search, X, Star, RefreshCw, Trash2, Bell } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DeleteAssessmentDialog } from "@/components/delete-assessment-dialog";
import { useQueryClient } from "@tanstack/react-query";


function ResendInviteButton({ assessment }: { assessment: AdminAssessment }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const canResend = assessment.status === "pending" || assessment.status === "in_progress" || assessment.status === "expired";
  const hasEmail = !!assessment.participantEmail;
  const wasExpired = assessment.status === "expired";

  const mutation = useMutation({
    mutationFn: ({ assessmentId }: { assessmentId: string }) =>
      customFetch(`/api/admin/assessments/${assessmentId}/resend-invite`, { method: "POST" }),
    onSuccess: () => {
      const description = wasExpired
        ? `Avaliação reativada e e-mail enviado para ${assessment.participantEmail}`
        : `E-mail enviado para ${assessment.participantEmail}`;
      toast({ title: "Convite reenviado!", description });
      queryClient.invalidateQueries({ queryKey: getListAdminAssessmentsQueryKey() });
    },
    onError: (err: unknown) => {
      const msg = (err as { data?: { error?: string } }).data?.error ?? "Erro ao reenviar convite";
      toast({ title: "Erro", description: msg, variant: "destructive" });
    },
  });

  if (!canResend) return null;

  const button = (
    <Button
      variant="ghost"
      size="sm"
      disabled={!hasEmail || mutation.isPending}
      onClick={(e) => {
        e.stopPropagation();
        mutation.mutate({ assessmentId: assessment.id });
      }}
      data-testid={`button-resend-invite-${assessment.id}`}
      className="h-7 px-2 text-xs"
    >
      <Send className="h-3.5 w-3.5 mr-1" />
      {mutation.isPending ? "Enviando…" : "Reenviar"}
    </Button>
  );

  if (!hasEmail) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{button}</span>
          </TooltipTrigger>
          <TooltipContent>
            <p>Avaliação sem e-mail cadastrado</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
}

function ResendPartnersButton({ assessment }: { assessment: AdminAssessment }) {
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: ({ assessmentId }: { assessmentId: string }) =>
      customFetch(`/api/admin/assessments/${assessmentId}/resend-partners-webhook`, { method: "POST" }),
    onSuccess: () => {
      toast({ title: "Webhook reenviado!", description: "Dados enviados para o partners com sucesso." });
    },
    onError: (err: unknown) => {
      const msg = (err as { data?: { error?: string } }).data?.error ?? "Erro ao reenviar para o partners";
      toast({ title: "Erro ao reenviar", description: msg, variant: "destructive" });
    },
  });

  if (assessment.status !== "completed") return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            disabled={mutation.isPending}
            onClick={(e) => {
              e.stopPropagation();
              mutation.mutate({ assessmentId: assessment.id });
            }}
            data-testid={`button-resend-partners-${assessment.id}`}
            className="h-7 px-2 text-xs"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${mutation.isPending ? "animate-spin" : ""}`} />
            {mutation.isPending ? "Enviando…" : "Partners"}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Reenviar dados para o partners</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function DeleteAssessmentButton({ assessment }: { assessment: AdminAssessment }) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const handleDeleted = () => {
    queryClient.invalidateQueries({ queryKey: getListAdminAssessmentsQueryKey() });
  };

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={(e) => { e.stopPropagation(); setOpen(true); }}
              data-testid={`button-delete-${assessment.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Excluir avaliação</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DeleteAssessmentDialog
        open={open}
        onOpenChange={setOpen}
        assessmentId={assessment.id}
        participantName={assessment.participantName}
        participantEmail={assessment.participantEmail}
        onDeleted={handleDeleted}
      />
    </>
  );
}

function AssessmentRow({ assessment }: { assessment: AdminAssessment }) {
  const [, navigate] = useLocation();
  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/30"
      onClick={() => navigate(`/admin/assessments/${assessment.id}`)}
      data-testid={`row-assessment-${assessment.id}`}
    >
      <TableCell className="pl-6 text-sm">{assessment.participantName ?? <span className="text-muted-foreground">—</span>}</TableCell>
      <TableCell className="text-sm">{assessment.participantEmail ?? <span className="text-muted-foreground">—</span>}</TableCell>
      <TableCell className="font-mono text-xs text-muted-foreground">{assessment.participantId}</TableCell>
      <TableCell className="font-mono text-xs text-muted-foreground" title={assessment.companyId ?? undefined}>
        {assessment.companyId
          ? `${assessment.companyId.slice(0, 8)}…`
          : <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="text-sm">{assessment.apiKeyName}</TableCell>
      <TableCell>
        <Badge variant="outline" className={STATUS_CLASS[assessment.status] ?? ""}>
          {STATUS_LABEL[assessment.status] ?? assessment.status}
        </Badge>
      </TableCell>
      <TableCell className="text-muted-foreground text-sm">
        {format(new Date(assessment.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
      </TableCell>
      <TableCell className="text-muted-foreground text-sm">
        {assessment.completedAt
          ? format(new Date(assessment.completedAt), "dd/MM/yyyy HH:mm", { locale: ptBR })
          : "—"}
      </TableCell>
      <TableCell className="text-sm font-mono">
        {assessment.totalTokens != null && assessment.totalTokens > 0
          ? assessment.totalTokens.toLocaleString("pt-BR")
          : <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="text-sm font-mono">
        {assessment.aiCostUsd != null && parseFloat(assessment.aiCostUsd) > 0
          ? `$${parseFloat(assessment.aiCostUsd).toFixed(4)}`
          : <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="text-sm" data-testid={`cell-rating-${assessment.id}`}>
        {assessment.participantRating != null ? (
          <span className="inline-flex items-center gap-1 font-medium">
            <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
            {assessment.participantRating}/5
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="pr-4 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1">
          {(assessment.status === "pending" || assessment.status === "in_progress") && assessment.chatUrl && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" asChild>
                    <a href={assessment.chatUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-3.5 w-3.5 mr-1" />
                      Chat
                    </a>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Abrir chat do participante</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <ResendInviteButton assessment={assessment} />
          <ResendPartnersButton assessment={assessment} />
          <DeleteAssessmentButton assessment={assessment} />
          <ChevronRight className="h-4 w-4 text-muted-foreground inline-block" />
        </div>
      </TableCell>
    </TableRow>
  );
}

function AssessmentsContent() {
  const { logout, authHeader } = useAdminAuth();
  const { toast } = useToast();
  const [bulkNotifyOpen, setBulkNotifyOpen] = useState(false);
  const [bulkNotifyLoading, setBulkNotifyLoading] = useState(false);

  const handleBulkNotify = async () => {
    setBulkNotifyLoading(true);
    try {
      const res = await fetch(
        `${import.meta.env.BASE_URL}api/admin/assessments/bulk-notify-partners`,
        { method: "POST", headers: { Authorization: authHeader ?? "" } },
      );
      const body = await res.json() as { total?: number; sent?: number; failed?: number };
      if (!res.ok) throw new Error((body as { error?: string }).error ?? `Erro ${res.status}`);
      toast({
        title: "Notificação enviada!",
        description: `${body.sent ?? 0} de ${body.total ?? 0} avaliações comunicadas aos partners.${(body.failed ?? 0) > 0 ? ` ${body.failed} falharam.` : ""}`,
      });
    } catch (err) {
      toast({ title: "Erro", description: err instanceof Error ? err.message : "Erro desconhecido", variant: "destructive" });
    } finally {
      setBulkNotifyLoading(false);
      setBulkNotifyOpen(false);
    }
  };

  const [companyIdInput, setCompanyIdInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [uuidInput, setUuidInput] = useState("");

  const debouncedCompanyId = useDebounce(companyIdInput, 400);
  const debouncedEmail = useDebounce(emailInput, 400);
  const debouncedUuid = useDebounce(uuidInput, 400);

  const params: ListAdminAssessmentsParams = {};
  if (debouncedCompanyId) params.companyId = debouncedCompanyId;
  if (debouncedEmail) params.email = debouncedEmail;
  if (debouncedUuid) params.participantId = debouncedUuid;

  const hasFilters = !!(debouncedCompanyId || debouncedEmail || debouncedUuid);

  const { data: assessments, isLoading, error } = useListAdminAssessments(params, {
    query: { queryKey: getListAdminAssessmentsQueryKey(params) },
  });

  const { data: allAssessments, isLoading: isLoadingAll } = useListAdminAssessments({}, {
    query: { queryKey: getListAdminAssessmentsQueryKey({}) },
  });

  useEffect(() => {
    if (error && (error as { status?: number }).status === 401) logout();
  }, [error, logout]);

  const clearFilters = () => {
    setCompanyIdInput("");
    setEmailInput("");
    setUuidInput("");
  };

  const ratedCompletedAssessments = allAssessments?.filter(
    (a) => a.status === "completed" && a.participantRating != null
  ) ?? [];
  const averageRating =
    ratedCompletedAssessments.length > 0
      ? ratedCompletedAssessments.reduce((sum, a) => sum + (a.participantRating ?? 0), 0) / ratedCompletedAssessments.length
      : null;

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-1">Sessões</h1>
          <p className="text-muted-foreground">Crie e acompanhe as entrevistas DISC dos participantes.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setBulkNotifyOpen(true)}
            data-testid="button-bulk-notify-partners"
          >
            <Bell className="h-3.5 w-3.5 mr-1.5" />
            Notificar partners
          </Button>
          <NewAssessmentDialog />
        </div>
      </div>

      <Card className="shadow-sm border-yellow-200 bg-yellow-50/30" data-testid="card-avg-rating">
        <CardContent className="py-4 px-6 flex items-center gap-4">
          <div className="flex items-center gap-2 text-yellow-500">
            <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Avaliação média</p>
            {isLoadingAll ? (
              <Skeleton className="h-6 w-20 mt-0.5" />
            ) : averageRating != null ? (
              <p className="text-xl font-semibold" data-testid="text-avg-rating">
                {averageRating.toFixed(1)}<span className="text-sm font-normal text-muted-foreground">/5</span>
                <span className="ml-2 text-xs font-normal text-muted-foreground">({ratedCompletedAssessments.length} avaliação{ratedCompletedAssessments.length !== 1 ? "ões" : ""})</span>
              </p>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="text-avg-rating">Nenhuma avaliação ainda</p>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Company ID</label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              data-testid="filter-company-id"
              placeholder="UUID da empresa"
              value={companyIdInput}
              onChange={(e) => setCompanyIdInput(e.target.value)}
              className="pl-8 h-9 w-64 text-sm"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">E-mail</label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              data-testid="filter-email"
              placeholder="email@exemplo.com"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              className="pl-8 h-9 w-64 text-sm"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">UUID do participante</label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              data-testid="filter-participant-uuid"
              placeholder="UUID do participante"
              value={uuidInput}
              onChange={(e) => setUuidInput(e.target.value)}
              className="pl-8 h-9 w-64 text-sm"
            />
          </div>
        </div>
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            data-testid="button-clear-filters"
            className="h-9 gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
            Limpar filtros
          </Button>
        )}
      </div>

      <Card className="shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">
            {hasFilters ? "Resultados filtrados" : "Todas as sessões"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow>
                <TableHead className="pl-6">Nome</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>UUID</TableHead>
                <TableHead>Company ID</TableHead>
                <TableHead>Chave de API</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Criada em</TableHead>
                <TableHead>Concluída em</TableHead>
                <TableHead>Tokens</TableHead>
                <TableHead>Custo (USD)</TableHead>
                <TableHead>Avaliação</TableHead>
                <TableHead className="pr-4" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell className="pl-6"><Skeleton className="h-5 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-40" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-40" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-28" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-28" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  <TableCell />
                </TableRow>
              )) : assessments?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} className="h-32 text-center text-muted-foreground" data-testid="text-no-sessions">
                    {hasFilters ? "Nenhuma sessão encontrada para os filtros aplicados." : "Nenhuma sessão encontrada. Crie uma para começar."}
                  </TableCell>
                </TableRow>
              ) : assessments?.map((a) => <AssessmentRow key={a.id} assessment={a} />)}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={bulkNotifyOpen} onOpenChange={setBulkNotifyOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Notificar partners</DialogTitle>
            <DialogDescription>
              Envia o evento <code className="text-xs bg-muted px-1 py-0.5 rounded">assessment.created</code> para todos os assessments com status <strong>pending</strong>, <strong>in_progress</strong> e <strong>expired</strong>. Esta ação pode demorar alguns segundos.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkNotifyOpen(false)} disabled={bulkNotifyLoading}>
              Cancelar
            </Button>
            <Button onClick={handleBulkNotify} disabled={bulkNotifyLoading}>
              {bulkNotifyLoading ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Enviando…</>
              ) : (
                <><Bell className="h-4 w-4 mr-2" />Confirmar</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AssessmentsPage() {
  return (
    <AdminLayout>
      <AssessmentsContent />
    </AdminLayout>
  );
}
